import * as React from "react";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import {Option} from "ts-option";
import Note from "../../../model/note/Note";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../store/action/actionHelpers";
import {createNote, updateNote} from "../../../store/action/note/notes";
import {Dispatch} from "redux";
import {connect} from "react-redux";
import Toast from "../../helper/Toast";
import UserMention from "../../mention/UserMention";
import {IMentionData} from "react-mentions";
import MentionUtils from "../../../utils/MentionUtils";
import {api} from "../../../api/api";
import _ = require("underscore");
import Checkbox from "../../../hiberUiKit/src/atoms/Checkbox/Checkbox";

interface IProps {
	/**
	 * Lead id for which form is created.
	 */
	leadId: string;
	/**
	 * Note to edit.
	 */
	noteToEdit?: Option<Note>;
	/**
	 * On cancel click.
	 */
	onCancel?: () => void;
	/**
	 * On note edit success
	 */
	onEditSuccess?: () => void;
}

interface DispatchProps {
	/**
	 * Add note action.
	 * @param {string} leadId lead identifier for which add note.
	 * @param {string} content note text.
	 * @param {string} extendedContent note extended text.
	 * @param {boolean} pinned is note pinned flag.
	 * @param {OnSuccessData<Note>} onSuccess on successful action callback.
	 * @param {OnFailure} onFailure on failure action callback.
	 */
	addNote: (leadId: string,
		content: string,
		extendedContent: string,
		pinned: boolean,
		onSuccess: OnSuccessData<Note>,
		onFailure: OnFailure) => void;
	/**
	 * Update note action
	 * @param {string} noteId note identifier.
	 * @param {string} leadId lead identifier.
	 * @param {string} content note content to set.
	 * @param {string} extendedContent note extended content to set.
	 * @param {boolean} pinned is note pinned flag.
	 * @param {OnSuccessData<Note>} onSuccess on successful update action callback.
	 * @param {OnFailure} onFailure on failure update action callback.
	 */
	updateNote: (noteId: string,
		leadId: string,
		content: string,
		extendedContent: string,
		onSuccess: OnSuccess,
		onFailure: OnFailure) => void;
}

type Props = IProps & DispatchProps

interface IState {
	/**
	 * Indicating if request to server is in progress.
	 */
	isLoading: boolean;
	/**
	 * Note content.
	 */
	noteContent: string;
	/**
	 * Extended note content.
	 */
	noteExtendedContent: string;
	/**
	 * Mentions in note content
	 */
	mentions: IMentionData[];
	/**
	 * Flag if note is pinned to top
	 */
	notePinned: boolean;
}

class NoteTab extends React.Component<Props, IState> {

	constructor(props: Props) {
		super(props);
		this.state = {
			isLoading: false,
			noteContent: "",
			noteExtendedContent: "",
			mentions: [],
			notePinned: false
		};
		this.onSubmit = this.onSubmit.bind(this);
		this.onFailure = this.onFailure.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onAddSuccess = this.onAddSuccess.bind(this);
		this.onEditSuccess = this.onEditSuccess.bind(this);
		this.mentionUsers = this.mentionUsers.bind(this);
	}

	componentDidMount() {
		this.props.noteToEdit.map(
			note => this.setState({
				noteContent: note.content,
				noteExtendedContent: note.extendedContent,
				mentions: MentionUtils.getMentions(note.extendedContent),
				notePinned: note.pinningData.pinned
			}));
	}

	componentWillReceiveProps(nextProps: Props) {
		nextProps.noteToEdit.map(
			note => this.setState({
				noteContent: note.content,
				noteExtendedContent: note.extendedContent,
				mentions: MentionUtils.getMentions(note.extendedContent),
				notePinned: note.pinningData.pinned
			}))
	}

	render() {
		return (
			<div className={"c-note-tab"}>
				<UserMention onChange={this.onChange}
							 value={this.state.noteExtendedContent}
							 placeholder={"Write a simple note..."}
							 onlyAgentsAdminsAndBdps/>
				{this.props.noteToEdit.isEmpty &&
				<Checkbox checked={this.state.notePinned} label={'Pin to top'}
						  onChange={() => this.setState({notePinned: !this.state.notePinned})}
						  disabled={this.state.isLoading || !this.state.noteExtendedContent.trim().length}
						  styles={{minWidth: '100px'}}/>
				}
				<ButtonPrimary label={this.props.noteToEdit.isDefined ? "Update Note" : "Add Note"}
							   onClick={this.onSubmit}
							   disabled={this.state.isLoading || !this.state.noteExtendedContent.trim().length}/>
			</div>
		);
	}

	private onChange(evt: React.ChangeEvent<HTMLTextAreaElement>, mentions: IMentionData[], plainText: string) {
		this.setState({
			noteExtendedContent: evt.target.value,
			noteContent: plainText,
			mentions,
			notePinned: !evt.target.value.length ? false : this.state.notePinned
		});
	}

	private onSubmit() {
		this.setState({isLoading: true});
		if (this.props.noteToEdit.isDefined) {
			const note = this.props.noteToEdit.get;
			this.props.updateNote(note.id,
				this.props.leadId,
				this.state.noteContent,
				this.state.noteExtendedContent,
				() => this.onEditSuccess(note.id), this.onFailure)
		}
		else {
			this.props.addNote(this.props.leadId,
				this.state.noteContent,
				this.state.noteExtendedContent,
				this.state.notePinned,
				this.onAddSuccess,
				this.onFailure)
		}
	}

	/**
	 * Adding new note success callback.
	 */
	private onAddSuccess(note: Note) {
		this.mentionUsers(note.id);
		this.setState({isLoading: false, noteContent: "", noteExtendedContent: "", mentions: [], notePinned: false});
		Toast.success("Note added successfully.", "Create Note");
	}

	/**
	 * Editing note success callback.
	 */
	private onEditSuccess(noteId: string) {
		this.mentionUsers(noteId);
		this.setState({isLoading: false, noteContent: "", noteExtendedContent: "", mentions: [], notePinned: false});
		this.props.onEditSuccess();
		Toast.success("Note edited successfully", "Edit Note");
	}

	private mentionUsers(noteId: string) {
		const uniqueUsers = _.uniq(this.state.mentions.map(m => m.id));
		if (uniqueUsers.length)
			api.instance.leadApi.mentionUsers(this.props.leadId, uniqueUsers, noteId).catch(
				() => Toast.error("Mention users error"));
	}

	/**
	 * Adding new note failure callback.
	 */
	private onFailure() {
		this.setState({isLoading: false});
		Toast.error("Sorry, something went wrong. Try again later.", "Create Note error");
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		addNote: (leadId: string,
			content: string,
			extendedContent: string,
			pinned: boolean,
			onSuccess: OnSuccessData<Note>,
			onFailure: OnFailure) =>
			dispatch(createNote(leadId, content, extendedContent, pinned, onSuccess, onFailure)),
		updateNote: (noteId: string, leadId: string, content: string, extendedContent: string, onSuccess: OnSuccess,
			onFailure: OnFailure) => dispatch(updateNote(noteId, leadId, content, extendedContent, onSuccess, onFailure))
	}
}

export default connect(null, mapDispatchToProps)(NoteTab)

